import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgentLayout from './AgentLayout';
import Icon from '../../components/AppIcon';
import { selectCustomerId } from '../../features/auth/authSlice';
import {
  useGetMyAgentPropertiesQuery,
  useDeleteAgentPropertyMutation,
  useGetMonetizationConfigQuery,
  useBoostPropertyMutation,
  useUpdatePropertyListingMutation,
} from '../../features/api/apiSlice';
import { useCurrency } from '../../features/currency/CurrencyContext';

const isTakenType = (p) => {
  const t = Number(p.propery_type ?? p.property_type);
  return t === 2 || t === 3;
};

const typeLabel = (p) => {
  const t = Number(p.propery_type ?? p.property_type);
  if (t === 1) return 'For Rent';
  if (t === 2) return 'Sold';
  if (t === 3) return 'Rented';
  return 'For Sale';
};

/** Toggle switch used for Active / Taken columns */
const FlagToggle = ({ checked, onChange, disabled, onLabel, offLabel, accent = 'emerald' }) => {
  const onColors =
    accent === 'amber'
      ? 'bg-amber-500'
      : accent === 'rose'
        ? 'bg-rose-500'
        : 'bg-emerald-500';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-[3.25rem] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 ${
        checked ? onColors : 'bg-slate-300'
      } focus:ring-slate-400`}
      title={checked ? onLabel : offLabel}
    >
      <span
        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-0'
        }`}
      />
    </button>
  );
};

const AgentProperties = () => {
  const customerId = useSelector(selectCustomerId);
  const { formatPrice } = useCurrency();
  const { data, isLoading, isFetching, refetch } = useGetMyAgentPropertiesQuery(
    { userid: customerId, limit: 100 },
    { skip: !customerId }
  );
  const [deleteProperty, { isLoading: deleting }] = useDeleteAgentPropertyMutation();
  const { data: monetization } = useGetMonetizationConfigQuery();
  const [boostProperty, { isLoading: boosting }] = useBoostPropertyMutation();
  const [updateListing] = useUpdatePropertyListingMutation();
  const [busyId, setBusyId] = useState(null);

  const properties = data?.data || [];

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property permanently?')) return;
    try {
      await deleteProperty(id).unwrap();
      refetch();
    } catch (e) {
      alert(e?.data?.message || 'Delete failed');
    }
  };

  const handleBoost = async (id) => {
    if (!monetization?.boost_enabled) {
      alert('Featured boosts are currently disabled by admin.');
      return;
    }
    const fee = monetization.boost_fee_ugx || 0;
    const days = monetization.boost_days || 7;
    if (
      !window.confirm(
        `Boost this property to the top of search for ${days} days?\nFee: UGX ${Number(
          fee
        ).toLocaleString()} (payment integration can mark paid).`
      )
    ) {
      return;
    }
    try {
      const res = await boostProperty({ property_id: id, mark_paid: true }).unwrap();
      alert(res?.message || 'Property boosted.');
      refetch();
    } catch (e) {
      alert(e?.data?.message || 'Boost failed');
    }
  };

  const handleActiveToggle = async (p, next) => {
    setBusyId(p.id);
    try {
      await updateListing({ property_id: p.id, active: next ? 1 : 0 }).unwrap();
      refetch();
    } catch (e) {
      alert(e?.data?.message || 'Could not update active status');
    } finally {
      setBusyId(null);
    }
  };

  const handleTakenToggle = async (p, next) => {
    setBusyId(p.id);
    try {
      await updateListing({ property_id: p.id, taken: next ? 1 : 0 }).unwrap();
      refetch();
    } catch (e) {
      alert(e?.data?.message || 'Could not update taken status');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AgentLayout
      title="My Properties"
      subtitle="Manage the properties you represent on HireBroker."
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <p className="text-sm text-slate-600">
          {data?.total_count != null
            ? `${data.total_count} propert${data.total_count === 1 ? 'y' : 'ies'}`
            : ''}
          <span className="text-slate-400"> · </span>
          <span className="text-slate-500">
            Active = live on the market · Taken = sold / rented
          </span>
        </p>
        <Link
          to="/agent-dashboard/properties/new"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
        >
          <Icon name="Plus" size={16} />
          Add Property
        </Link>
      </div>

      {isLoading || isFetching ? (
        <p className="text-slate-500">Loading your portfolio…</p>
      ) : properties.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Icon name="Building2" size={48} className="mx-auto text-slate-300 mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">No properties yet</h2>
          <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
            Publish rentals, sales, and land. They appear on the public market once live.
          </p>
          <Link
            to="/agent-dashboard/properties/new"
            className="inline-flex px-5 py-2.5 rounded-lg bg-amber-500 text-slate-950 font-semibold text-sm"
          >
            Add first property
          </Link>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Property</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Price</th>
                  <th className="px-4 py-3 font-semibold text-center">Active</th>
                  <th className="px-4 py-3 font-semibold text-center">Taken</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {properties.map((p) => {
                  const active = Number(p.status) === 1;
                  const taken = isTakenType(p);
                  const busy = busyId === p.id;
                  return (
                    <tr key={p.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-[200px]">
                          <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                            {p.title_image ? (
                              <img
                                src={p.title_image}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : null}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{p.title}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {p.city || p.address || p.slug_id || `#${p.id}`}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {typeLabel(p)}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">
                        {p.price != null ? formatPrice(p.price, 'UGX') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          <FlagToggle
                            checked={active}
                            disabled={busy}
                            onLabel="Active (live)"
                            offLabel="Inactive"
                            accent="emerald"
                            onChange={(v) => handleActiveToggle(p, v)}
                          />
                          <span
                            className={`text-[10px] font-semibold uppercase tracking-wide ${
                              active ? 'text-emerald-600' : 'text-slate-400'
                            }`}
                          >
                            {active ? 'On' : 'Off'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          <FlagToggle
                            checked={taken}
                            disabled={busy}
                            onLabel="Taken (sold/rented)"
                            offLabel="Available"
                            accent="rose"
                            onChange={(v) => handleTakenToggle(p, v)}
                          />
                          <span
                            className={`text-[10px] font-semibold uppercase tracking-wide ${
                              taken ? 'text-rose-600' : 'text-slate-400'
                            }`}
                          >
                            {taken ? 'Taken' : 'Open'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {p.slug_id && (
                          <Link
                            to={`/property/${p.slug_id}`}
                            className="text-xs font-medium text-slate-600 hover:text-slate-900 mr-3"
                          >
                            View
                          </Link>
                        )}
                        {monetization?.boost_enabled && (
                          <button
                            type="button"
                            disabled={boosting}
                            onClick={() => handleBoost(p.id)}
                            className="text-xs font-medium text-amber-700 hover:text-amber-800 mr-3"
                          >
                            Boost
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={deleting}
                          onClick={() => handleDelete(p.id)}
                          className="text-xs font-medium text-red-600 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AgentLayout>
  );
};

export default AgentProperties;
